/**
 * DateInput — controlled `<input type="date">` with a defensive
 * onBlur fallback that re-syncs the parent's state from the DOM value.
 *
 * Closes #250 round 8 (4th reopen). The bug history:
 *
 *   - Round 5 / round 6 / round 7: each iteration fixed one helper
 *     (`normalizeDateOnly`, `todayLocalIso`, `isDateOnlyDirty`) and
 *     declared the bug closed. Every retest still failed.
 *   - Round 8 root cause: native `<input type="date">` does NOT
 *     reliably fire React's synthetic `onChange` for every input
 *     transition. Specifically, when an automation framework (or a
 *     user using keyboard-only input) sets the date via the picker,
 *     some browsers commit the value to the DOM but skip the change
 *     event React listens for. The form state stays at the previous
 *     default while the DOM shows the operator's typed date.
 *   - Three rounds of fixing date helpers in isolation could not see
 *     this because the helpers ARE correct. The gap is between the
 *     DOM and the React state, before any helper runs.
 *
 * The fix is defense-in-depth at the input layer:
 *   1. `onChange` keeps the controlled value in sync when it fires.
 *   2. `onBlur` fires whenever focus leaves the input — including
 *      when the operator clicks Next / Save / Submit. If the DOM
 *      value diverged from React state for any reason (missed
 *      change event, programmatic value set, native picker quirks),
 *      the blur handler re-syncs by calling `onChange(domValue)`.
 *   3. `forwardRef` exposes the underlying DOM input so the parent
 *      can read the live DOM value at submit time as a final check
 *      (the Edit dialog does this in handleSave).
 *
 * This component must be the only path for date-only inputs in the
 * Users module. Any direct `<Input type="date">` site is a regression
 * vector for #250.
 */

import * as React from 'react';
import { Input } from '@/components/ui/input';

interface DateInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'> {
  /** Controlled YYYY-MM-DD value. Empty string = unset. */
  value: string;
  /** Called whenever the DOM value diverges from `value` — via
   *  onChange OR onBlur. Always passes a YYYY-MM-DD string or ''. */
  onChange: (value: string) => void;
}

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  function DateInput({ value, onChange, onBlur, ...rest }, ref) {
    return (
      <Input
        ref={ref}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => {
          // Defensive sync: if the DOM value diverged from React
          // state for any reason (missed change event, programmatic
          // value set, native picker quirks), force a sync now.
          // Idempotent — when DOM === state, this is a no-op.
          if (e.target.value !== value) {
            onChange(e.target.value);
          }
          onBlur?.(e);
        }}
        {...rest}
      />
    );
  }
);
